import { Component, ElementRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import * as L from 'leaflet';
import { Marker } from 'leaflet';
import { takeUntil } from 'rxjs';
import { DeviceService } from '../../../services/device.service';
import { MapIconService } from '../../../services/map-icon.service';
import { PageLoaderService } from '../../../services/page-loader.service';
import { DialogBase } from '../../../shared/components/dialogs/dialog-base/dialog-base';
import { AbstractMap } from '../../../shared/components/map/map';
import { formatTimeAgoTranslation} from '../../../shared/utils/date.util';
import {
  getActiveAlarmsSorted,
  getAlarmIconCode,
  getAlarmTypeColor,
} from '../../../shared/utils/map/map-marker.util';
import { AlarmDto } from '../../../types/dtos/alarm.dto';
import { DeviceDto } from '../../../types/dtos/device.dto';
import { DeviceMapDto } from '../../../types/dtos/map.dto';
import { AlarmStatus } from '../../../types/enums/alarm.enum';
import { DialogDeviceDetail } from '../../devices/dialog-device-detail/dialog-device-detail';
import {TranslateService} from '@ngx-translate/core';
import {TranslateSafePipe} from '../../../shared/pipes/translation.pipe';
import {LoggingActionEnum, LoggingTargetEnum} from '../../../types/enums/logging.enum';
import {LogService} from '../../../services/log.service';

@Component({
  selector: 'app-map',
  imports: [
    DialogBase,
    DialogDeviceDetail,
    TranslateSafePipe,
  ],
  templateUrl: './map.component.html',
  styleUrl: './map.component.css',
})
export class MapComponent extends AbstractMap {
  @ViewChild('mapContainer', { static: true })
  public mapContainer!: ElementRef<HTMLDivElement>;

  @ViewChild('showDeviceDetails', { static: true })
  public showDeviceDetails!: DialogDeviceDetail;

  public selectedDevice: DeviceDto | null = null;
  public isDeviceDetailOpen: boolean = false;

  constructor(
    override readonly _deviceService: DeviceService,
    override readonly _mapIconService: MapIconService,
    private readonly _pageLoaderService: PageLoaderService,
    private readonly _router: Router,
    private readonly _translateService: TranslateService,
    private readonly _logService: LogService,
  ) {
    super(_deviceService, _mapIconService);
  }

  public ngOnInit(): void {
    this._deviceService.devices$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (devices: DeviceDto[]) => {
          this.updateDevicesToMapDevices(devices);
          this.addMarkers();
        },
      });
  }

  public async ngAfterViewInit(): Promise<void> {
    await this._pageLoaderService.waitLoadingFinish();
    this._map = L.map(this.mapContainer.nativeElement, {
      center: this._deviceService.getCenter(),
      zoom: 13,
    });

    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      maxZoom: 19,
      attribution: '&copy; OpenStreetMap contributors',
    })
      .addTo(this._map);

    this.markerLayer.addTo(this._map);

    this.resizeObserver = new ResizeObserver(() => {
      this._map.invalidateSize();
    });

    this.resizeObserver.observe(this.mapContainer.nativeElement);

    this.addMarkers();
    this._fitMapToDevices();
  }

  public openShowDetails(device: DeviceDto): void {
    this.selectedDevice = device;
    this.isDeviceDetailOpen = true;
  }

  public readonly closeShowDetails = (): void => {
    this.isDeviceDetailOpen = false;
  };

  override updateMarker(marker: Marker, device: DeviceMapDto) {
    marker.bindPopup(`
<div class="device-popup-content">
  <div class="left-side">
    <p class="serial-number">
      ${this._translateService.instant('common.fields.device')}:
    </p>

    <p class="status">
      ${this._translateService.instant('common.fields.status')}:
    </p>

    ${ this._getActiveAlarm(device, true) }

    <p class="index">
      Index:
    </p>
  </div>

  <div class="right-side">
    <p class="serial-number">
      ${ device.serial_number ?? '-' }
    </p>

    <p class="status ${ this._deviceService.getOnlineStatus(device) }">
      <span class="status-icon"></span>

      ${ formatTimeAgoTranslation(this._translateService,device.last_measurement_received_time!) }
    </p>

    ${ this._getActiveAlarm(device, false) }

    <p class="index">
      ${ this.getDeviceValue(device) }
    </p>
  </div>
</div>

<div class="button-container">
  <button type="button" class="button-show-details">
    ${this._translateService.instant('common.action.show-more')}
  </button>
</div>
    `);

    marker.on('popupopen', () => {
      const popupElement: HTMLElement | undefined = marker.getPopup()
        ?.getElement();

      this._logService.sendLog({
        target: LoggingTargetEnum.DEVICE_MAP,
        action: LoggingActionEnum.VIEW,
        target_id: device.id,
      });

      const detailsButton: Element | null | undefined = popupElement?.querySelector('.button-show-details');

      if (!detailsButton) {
        return;
      }

      detailsButton.addEventListener('click', () => {
        this.openShowDetails(device);
      });

      this._bindAlarmIconClicks(popupElement!);
    });
  }

  private _getActiveAlarmIcons(device: DeviceDto): string {
    const activeAlarms: AlarmDto[] = getActiveAlarmsSorted(device);

    if (activeAlarms.length === 0) {
      return '';
    }

    const iconSize: number = this.getMarkerIconSize();
    const maxIcons = 4;

    const visibleAlarms: AlarmDto[] = activeAlarms.slice(0, maxIcons);
    const hasMore: boolean = activeAlarms.length > maxIcons;

    const iconsHtml: string = visibleAlarms
      .map((alarm) => {
        const color: string = getAlarmTypeColor(alarm.alarm_type);
        const iconCode: string = getAlarmIconCode(alarm);

        return `
        <button
          type="button"
          class="alarm-icon"
          data-alarm-id="${alarm.id}">
          ${this._mapIconService.renderIcon(color, iconCode, iconSize)}
        </button>
      `;
      })
      .join('');

    const ellipsisHtml = hasMore
                         ? `<span class="alarm-icon alarm-icon-more">...</span>`
                         : '';

    return `${ iconsHtml }${ ellipsisHtml }`;
  }

  private _getActiveAlarm(device: DeviceDto, isLeftSide: boolean): string {
    const activeAlarms = device.alarms?.filter((alarm) => alarm.status !== AlarmStatus.RESOLVED);

    if (!activeAlarms || activeAlarms.length === 0) {
      return '';
    }

    if (isLeftSide) {
      return `
      <p class="alarm">
        ${this._translateService.instant('common.fields.alarms')}:
      </p>
      `;
    } else {
      return `
      <div class="alarm">
        ${ this._getActiveAlarmIcons(device) }
      </div>
      `;
    }
  }

  private _bindAlarmIconClicks(container: HTMLElement): void {
    const alarmButtons = container.querySelectorAll('[data-alarm-id]');

    alarmButtons.forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();

        const alarmId = (event.currentTarget as HTMLElement).getAttribute('data-alarm-id');
        if (!alarmId) return;

        this._router.navigate(['/alarms'], {
          queryParams: { id: alarmId },
        });
      });
    });
  }
}
